import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, X, Volume2, VolumeX, Users2, Wallet, ShieldCheck, Trophy, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MEDIA = `${BACKEND_URL}/api/media/demo`;

/**
 * DemoVideoPlayer
 *
 * A hybrid 35-40 second "platform breakdown" video experience:
 *  1) Sora 2 hero opener  (mp4, ~8s)
 *  2) Animated walkthrough (in-browser motion graphics with framer-motion, ~24s)
 *  3) Sora 2 celebration outro (mp4, ~4s)
 *
 * OpenAI TTS narration plays continuously across all three segments. Captions
 * update per timestamp. Works as an inline card and as a fullscreen modal.
 *
 * Props:
 *   variant: 'card' | 'modal'
 *   onClose: () => void  (only when variant='modal')
 */
export default function DemoVideoPlayer({ variant = 'card', onClose }) {
  const [phase, setPhase] = useState('idle');       // idle | hero | walkthrough | outro | done
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);      // 0..1 across full timeline
  const [captionIdx, setCaptionIdx] = useState(0);

  const audioRef = useRef(null);
  const heroVidRef = useRef(null);
  const outroVidRef = useRef(null);
  const timelineTimerRef = useRef(null);

  // Timeline (seconds) — narration is ~35s total. Segments:
  //   0-8s   hero clip
  //   8-32s  animated walkthrough (24s split across 5 beats)
  //   32-36s outro clip
  const TIMELINE_TOTAL = 36;
  const HERO_END = 8;
  const OUTRO_START = 32;

  const CAPTIONS = [
    { t: 0.0, text: 'Susu — one of the oldest ways to save, together.' },
    { t: 6.0, text: 'Join a circle. Contribute each round.' },
    { t: 12.0, text: 'Take your turn receiving the pot.' },
    { t: 17.0, text: 'No interest. No loans. Just community.' },
    { t: 22.0, text: 'Every member is bank-verified via Plaid.' },
    { t: 27.0, text: 'Backed by an encrypted deposit + Trust Score.' },
    { t: 32.0, text: 'Where community meets capital.' },
  ];

  // Walkthrough beats — each roughly 5s of the 24s middle
  const BEATS = [
    { Icon: Users2,     tint: 'text-lime-400',   title: 'Join a circle',       body: '3 to 50 friends save together in one rotation.' },
    { Icon: Wallet,     tint: 'text-amber-400',  title: 'Contribute',          body: 'Weekly, biweekly, or monthly — you decide.' },
    { Icon: Trophy,     tint: 'text-yellow-300', title: 'Take your turn',      body: 'When your round comes up, receive the full pot.' },
    { Icon: ShieldCheck,tint: 'text-emerald-400',title: 'Bank-verified',       body: 'Plaid + Stripe + PayPal keep every member safe.' },
    { Icon: MessageSquare,tint:'text-cyan-400',   title: 'Chat with your circle', body: 'AI-moderated, PCI-safe, always your people.' },
  ];
  const beatIdx = Math.min(
    BEATS.length - 1,
    Math.floor(((progress * TIMELINE_TOTAL) - HERO_END) / ((OUTRO_START - HERO_END) / BEATS.length))
  );

  // ---------- WS connection lifecycle (n/a for this component) --------

  // Explicitly load videos on mount so browsers that don't honour preload="auto"
  // (or headless browsers that pause everything) still buffer the clips.
  useEffect(() => {
    heroVidRef.current?.load();
    outroVidRef.current?.load();
  }, []);

  // ---------- Playback control -----------------------------------------
  // NOTE: browsers require a user-gesture-synchronous play() call for
  // unmuted audio. If we `await` first, the gesture is lost. So we launch
  // both media element .play() promises synchronously and catch failures.
  const start = useCallback(() => {
    setPhase('hero');
    setPlaying(true);

    const audio = audioRef.current;
    const hero = heroVidRef.current;

    if (hero) {
      hero.currentTime = 0;
      hero.play().catch(() => {});
    }
    if (audio) {
      audio.currentTime = 0;
      audio.muted = muted;
      const p = audio.play();
      if (p && p.catch) {
        p.catch(() => {
          // Fallback: autoplay policy blocked unmuted audio. Retry muted so
          // the video still plays; user can hit the unmute button.
          audio.muted = true;
          setMuted(true);
          audio.play().catch(() => {});
        });
      }
    }
  }, [muted]);

  const pause = useCallback(() => {
    setPlaying(false);
    heroVidRef.current?.pause();
    outroVidRef.current?.pause();
    audioRef.current?.pause();
  }, []);

  const resume = useCallback(async () => {
    setPlaying(true);
    try {
      if (phase === 'hero') await heroVidRef.current?.play();
      if (phase === 'outro') await outroVidRef.current?.play();
      await audioRef.current?.play();
    } catch (e) { /* nothing to do */ }
  }, [phase]);

  const reset = useCallback(() => {
    pause();
    setPhase('idle');
    setProgress(0);
    setCaptionIdx(0);
    if (heroVidRef.current) heroVidRef.current.currentTime = 0;
    if (outroVidRef.current) outroVidRef.current.currentTime = 0;
    if (audioRef.current) audioRef.current.currentTime = 0;
  }, [pause]);

  // ---------- Master timeline driven by the audio element --------------
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      const t = audio.currentTime;
      const p = Math.min(1, t / TIMELINE_TOTAL);
      setProgress(p);
      // Phase transitions
      if (t < HERO_END) {
        setPhase((cur) => (cur === 'walkthrough' || cur === 'outro' ? 'hero' : cur));
      } else if (t >= HERO_END && t < OUTRO_START) {
        setPhase('walkthrough');
        // Pause the hero video once we're past it (Sora only 8s anyway)
      } else if (t >= OUTRO_START) {
        setPhase((cur) => {
          if (cur !== 'outro' && outroVidRef.current) {
            outroVidRef.current.currentTime = 0;
            outroVidRef.current.play().catch(() => {});
          }
          return 'outro';
        });
      }
      // Update caption
      let idx = 0;
      for (let i = 0; i < CAPTIONS.length; i++) if (t >= CAPTIONS[i].t) idx = i;
      setCaptionIdx(idx);
    };
    const onEnd = () => { setPhase('done'); setPlaying(false); };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnd);
    };
  }, []);

  // ---------- Mute toggle sync ----------------------------------------
  useEffect(() => { if (audioRef.current) audioRef.current.muted = muted; }, [muted]);

  // ---------- Render ---------------------------------------------------
  const containerClass = variant === 'modal'
    ? 'fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-6'
    : 'relative w-full';

  const frameClass = variant === 'modal'
    ? 'w-full max-w-5xl aspect-video rounded-2xl overflow-hidden bg-black border border-white/10'
    : 'w-full aspect-video rounded-2xl overflow-hidden bg-black border border-white/10';

  return (
    <div className={containerClass} data-testid={`demo-video-${variant}`}>
      <div className={variant === 'modal' ? 'w-full max-w-5xl flex flex-col gap-3' : 'w-full flex flex-col gap-3'}>
        <div className={frameClass}>
          {/* Hidden but always-mounted audio drives the timeline */}
          <audio ref={audioRef} src={`${MEDIA}/susu_narration.mp3`} preload="auto" />

          {/* Segment layers */}
          <div className="relative w-full h-full">
          {/* Hero */}
          <video
            ref={heroVidRef}
            src={`${MEDIA}/susu_hero.mp4`}
            poster={`${MEDIA}/susu_poster.jpg`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${phase === 'hero' ? 'opacity-100' : 'opacity-0'}`}
            playsInline
            muted
            preload="auto"
          />

          {/* Walkthrough */}
          <div className={`absolute inset-0 transition-opacity duration-500 ${phase === 'walkthrough' ? 'opacity-100' : 'opacity-0'} bg-[radial-gradient(circle_at_20%_20%,#1a3d1a_0%,#0A0A0A_60%)]`}>
            <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(132,204,22,.3)_1px,transparent_1px),linear-gradient(90deg,rgba(132,204,22,.3)_1px,transparent_1px)] [background-size:40px_40px]"></div>
            <div className="relative h-full flex items-center justify-center">
              <AnimatePresence mode="wait">
                {phase === 'walkthrough' && (
                  <motion.div
                    key={beatIdx}
                    initial={{ opacity: 0, y: 30, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -30, scale: 0.9 }}
                    transition={{ duration: 0.5 }}
                    className="text-center px-8"
                  >
                    <div className={`inline-flex w-24 h-24 rounded-2xl bg-white/5 border border-white/10 items-center justify-center mb-6 ${BEATS[beatIdx].tint}`}>
                      {(() => {
                        const I = BEATS[beatIdx].Icon;
                        return <I className="w-12 h-12" strokeWidth={1.5} />;
                      })()}
                    </div>
                    <div className="text-4xl md:text-5xl font-black tracking-tight text-white mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                      {BEATS[beatIdx].title}
                    </div>
                    <div className="text-lg md:text-xl text-neutral-300 max-w-xl mx-auto">
                      {BEATS[beatIdx].body}
                    </div>
                    <div className="mt-6 flex justify-center gap-1.5">
                      {BEATS.map((_, i) => (
                        <div key={i} className={`h-1 rounded-full transition-all duration-500 ${i === beatIdx ? 'w-8 bg-lime-400' : 'w-4 bg-white/20'}`} />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Outro */}
          <video
            ref={outroVidRef}
            src={`${MEDIA}/susu_outro.mp4`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${phase === 'outro' || phase === 'done' ? 'opacity-100' : 'opacity-0'}`}
            playsInline
            muted
            preload="auto"
          />

          {/* Idle poster — the entire surface is clickable */}
          {(phase === 'idle' || phase === 'done') && (
            <button
              onClick={phase === 'done' ? () => { reset(); start(); } : start}
              className="group absolute inset-0 w-full h-full block cursor-pointer focus:outline-none focus:ring-4 focus:ring-lime-500/50"
              data-testid={`demo-video-${variant}-play-cta`}
              aria-label={phase === 'done' ? 'Watch the demo again' : 'Play the 35-second Susu demo video'}
            >
              {/* Poster image */}
              <img
                src={`${MEDIA}/susu_poster.jpg`}
                alt="Susu community demo preview"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              {/* Vignette so the CTA reads on any poster color */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/40 transition-colors duration-300 group-hover:from-black/70 group-hover:via-black/20"></div>
              {/* Pulsing play ring */}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
                <div className="relative">
                  <span className="absolute inset-0 rounded-full bg-lime-400/40 animate-ping"></span>
                  <span className="relative flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-lime-500 group-hover:bg-lime-400 text-black shadow-2xl transition-transform group-hover:scale-110">
                    <Play className="w-8 h-8 md:w-10 md:h-10 ml-1" fill="currentColor" />
                  </span>
                </div>
                <div className="px-5 py-2 rounded-full bg-black/70 border border-white/10 backdrop-blur-md text-white font-black text-sm md:text-base tracking-wide" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {phase === 'done' ? 'WATCH AGAIN' : 'WATCH THE 35-SECOND DEMO'}
                </div>
                <div className="text-[11px] uppercase tracking-widest text-white/70 font-semibold">
                  {phase === 'done' ? '' : 'Voiceover + captions · 35 sec'}
                </div>
              </div>
            </button>
          )}

          {/* Bottom controls */}
          {phase !== 'idle' && (
            <div className="absolute bottom-0 left-0 right-0 h-12 flex items-center px-4 gap-3 bg-gradient-to-t from-black/90 to-transparent">
              <button
                onClick={playing ? pause : resume}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                data-testid="demo-video-play-pause"
              >
                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setMuted((v) => !v)}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                data-testid="demo-video-mute"
              >
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-lime-400 transition-all duration-200" style={{ width: `${progress * 100}%` }} />
              </div>
              {variant === 'modal' && (
                <button
                  onClick={() => { pause(); onClose?.(); }}
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
                  data-testid="demo-video-close"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          {/* Compliance micro-copy */}
          <div className="absolute top-3 right-3 text-[10px] text-white/60 bg-black/60 border border-white/10 rounded-full px-2 py-0.5 pointer-events-none">
            Illustrative — participation in a Susu depends on your circle
          </div>
        </div>
      </div>

      {/* Caption strip — LIVES BELOW the video, never blocks the picture */}
      <div
        className="w-full min-h-[52px] flex items-center justify-center px-4 py-2 rounded-xl bg-black/60 border border-white/10 backdrop-blur-md"
        aria-live="polite"
        data-testid="demo-video-caption-strip"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={captionIdx + '-' + phase}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            className="text-white text-center text-base md:text-lg font-medium max-w-3xl"
            data-testid="demo-video-caption"
          >
            {phase === 'idle'
              ? 'Captions will appear here as the video plays.'
              : (CAPTIONS[captionIdx]?.text || '')}
          </motion.div>
        </AnimatePresence>
      </div>
      </div>
    </div>
  );
}
